import {
  GetSavedItemsParams,
  getSavedItemsSummary,
} from "@repo/apis/customers-saveditems-v3";
import { ChameleonRequest, is404Error } from "@repo/chameleon-request";
import {
  addSavedItemsInitializer,
  removeSavedItemsInitializer,
  updateSavedItemsInitializer,
  getSavedItemsInitializer,
  getSavedItemsSummaryInitializer,
} from "./methods";
import { initializeMethod } from "./utils/method-initializer";
import type { SavedItemsState } from "./utils/state.types";

export type SavedItemsSdkConfig = Omit<
  GetSavedItemsParams,
  "authHeader" | "expand"
> & {
  chameleonRequestInstance: ChameleonRequest;
  keyStoreDataversion: string;
  store: string;
  currency: string;
  sizeSchema: string;
  shop: string;
  lang: string;
  getAuthHeader: () => Promise<string>;
};

export type SavedItemsSdkInstance = {
  initialSavedItems: SavedItemsState;
  addSavedItems: ReturnType<typeof addSavedItemsInitializer>;
  removeSavedItems: ReturnType<typeof removeSavedItemsInitializer>;
  updateSavedItems: ReturnType<typeof updateSavedItemsInitializer>;
  getSavedItems: ReturnType<typeof getSavedItemsInitializer>;
  getSavedItemsSummary: ReturnType<typeof getSavedItemsSummaryInitializer>;
  getSavedItemsState: () => SavedItemsState;
  setSavedItemsState: (newItems: SavedItemsState) => void;
};

export const initializeSavedItemsSdk = async (
  config: SavedItemsSdkConfig,
): Promise<SavedItemsSdkInstance> => {
  const { chameleonRequestInstance, getAuthHeader } = config;
  const authHeader = await getAuthHeader();

  let savedItems: SavedItemsState = null;

  try {
    const response = await getSavedItemsSummary({
      chameleonRequestInstance,
      authHeader,
      store: config.store,
      keyStoreDataversion: config.keyStoreDataversion,
      shop: config.shop,
      lang: config.lang,
    });

    savedItems = {
      ...response,
      isAnonymousCustomer: true,
      savedItemProducts: [],
    };
  } catch (error) {
    if (!is404Error(error)) {
      console.error("Error initializing Saved Items SDK:", error);
      throw error;
    }
  }

  const getSavedItemsState = () => savedItems;
  const setSavedItemsState = (newItems: SavedItemsState) => {
    savedItems = newItems;
  };

  const context = {
    ...config,
    getAuthHeader,
    state: {
      getSavedItemsState,
      setSavedItemsState,
    },
  };

  return {
    initialSavedItems: savedItems,
    addSavedItems: initializeMethod(context, addSavedItemsInitializer),
    removeSavedItems: initializeMethod(context, removeSavedItemsInitializer),
    updateSavedItems: initializeMethod(context, updateSavedItemsInitializer),
    getSavedItems: initializeMethod(context, getSavedItemsInitializer),
    getSavedItemsSummary: initializeMethod(
      context,
      getSavedItemsSummaryInitializer,
    ),
    getSavedItemsState,
    setSavedItemsState,
  };
};
